import {
  Box,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type {
  SchemeManifest,
  SchemeTokenOverrides,
  FontRole,
} from "@shared/types/foyer";
import { FONT_CATALOGUE, fontOptionForStack } from "@shared/schemes/fonts";
import { fieldGrid } from "./editorLayout";

interface Props {
  manifest: SchemeManifest;
  value: SchemeTokenOverrides | undefined;
  onChange: (next: SchemeTokenOverrides) => void;
}

const CUSTOM = "__custom__";

const ROLE_LABEL: Record<string, string> = {
  display: "Display",
  serif: "Serif / headings",
  sans: "Sans / body",
  mono: "Mono / labels",
};

/**
 * Pick the font for each of the template's text roles from a curated catalogue
 * (all already loaded, so they render immediately), with a "Custom…" escape to
 * a raw CSS font-family stack. Writes a sparse `tokens.fonts` diff. Changing a
 * role updates every text element that uses it.
 */
export default function TypographyEditor({ manifest, value, onChange }: Props) {
  const defaults = manifest.defaultTokens;
  const fontRoles = Object.keys(defaults.fonts) as FontRole[];
  const setFont = (role: FontRole, v: string) =>
    onChange({ ...value, fonts: { ...value?.fonts, [role]: v } });
  const fontVal = (role: FontRole) =>
    value?.fonts?.[role] ?? defaults.fonts[role] ?? "";

  const groups: { label: string; cat: "serif" | "sans" | "mono" }[] = [
    { label: "Serif", cat: "serif" },
    { label: "Sans", cat: "sans" },
    { label: "Mono", cat: "mono" },
  ];

  return (
    <Stack spacing={2}>
      <Typography variant="h6">Typography</Typography>
      <Typography variant="body2" color="text.secondary">
        Choose the font for each text role. A change applies everywhere that
        role is used. Colours live under <strong>Design</strong>.
      </Typography>
      <Box sx={fieldGrid(320)}>
        {fontRoles.map((role) => {
          const v = fontVal(role);
          const matched = fontOptionForStack(v);
          return (
            <Box key={role}>
              <TextField
                select
                size="small"
                fullWidth
                label={ROLE_LABEL[role] ?? role}
                value={matched ? matched.stack : CUSTOM}
                onChange={(e) => setFont(role, e.target.value)}
              >
                {groups.flatMap((g) => [
                  <ListSubheader key={`h-${g.cat}`}>{g.label}</ListSubheader>,
                  ...FONT_CATALOGUE.filter((f) => f.category === g.cat).map(
                    (f) => (
                      <MenuItem
                        key={f.stack}
                        value={f.stack}
                        sx={{ fontFamily: f.stack }}
                      >
                        {f.label}
                      </MenuItem>
                    )
                  ),
                ])}
                <ListSubheader key="h-other">Other</ListSubheader>
                <MenuItem value={CUSTOM}>Custom…</MenuItem>
              </TextField>
              {!matched && (
                <TextField
                  size="small"
                  fullWidth
                  sx={{ mt: 1 }}
                  label="Custom CSS font-family"
                  value={v}
                  onChange={(e) => setFont(role, e.target.value)}
                  helperText='e.g. "Playfair Display", Georgia, serif'
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
}
